import styled from "@emotion/styled";
import { FC } from "react";
import { FieldInputProps } from "react-final-form";

import { IPickerListItem, NumericInput, Picker, SpacingToken } from "@livechat/design-system-react-components";

import { Author } from "interfaces/entities/ticket";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { IRuleTriggerEventCount } from "store/entities/rules/interfaces";

import { EqualityLabel, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerEventCount>;
}

const ANYONE_KEY = "anyone";

export const EventCountTrigger: FC<IProps> = ({ input }) => {
  const eventOptions: IPickerListItem[] = [
    { key: "message", name: "message sent" },
    { key: "ratingRequestSent", name: "rating request sent" },
    { key: "followUpSent", name: "follow-up sent" },
  ];

  const selectedEventOptions = eventOptions.filter(option => input.value.eventTypeIn.includes(option.key));

  const handleEventSelect = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { eventTypeIn: [] });
      return;
    }

    const newEventTypeIn = selectedOptions.map(option => option.key);
    const selectedEventsContainsRatingOrFollowUp =
      newEventTypeIn.includes("ratingRequestSent") || newEventTypeIn.includes("followUpSent");

    changeTriggerValues(input, {
      eventTypeIn: newEventTypeIn,
      authorType: selectedEventsContainsRatingOrFollowUp ? Author.Rule : input.value.authorType,
    });
  };

  const authorOptions: IPickerListItem[] =
    input.value.eventTypeIn.includes("ratingRequestSent") || input.value.eventTypeIn.includes("followUpSent")
      ? [createAuthorOption(Author.Rule)]
      : [ANYONE_KEY, ...Object.values(Author)].map(createAuthorOption);

  const selectedAuthorOptions: IPickerListItem[] = input.value.authorType
    ? authorOptions.filter(option => input.value.authorType === option.key)
    : [createAuthorOption(ANYONE_KEY)];

  const handleAuthorSelect = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0 || selectedOptions[0].key === ANYONE_KEY) {
      changeTriggerValues(input, { authorType: undefined });
      return;
    }

    const newAuthorType = selectedOptions[0].key as Author;
    changeTriggerValues(input, { authorType: newAuthorType });
  };

  const equalityOptions = ["eq", "gt", "lt", "gte", "lte"].map(createEqualityOption);

  const eventsNumber = input.value.eq ?? input.value.gt ?? input.value.lt ?? input.value.gte ?? input.value.lte ?? 0;

  const handleEventsNumberChange = (value: string): void => {
    const operator = getSelectedEqualityOption(input.value);

    changeTriggerValues(
      input,
      {
        authorType: input.value.authorType,
        eventTypeIn: input.value.eventTypeIn,
        [operator]: value === "" ? 0 : parseInt(value, 10),
      },
      { overwrite: true }
    );
  };

  const selectedEqualityOption = [createEqualityOption(getSelectedEqualityOption(input.value))];

  const handleEqualityOptionSelect = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { eq: 1, gt: undefined, lt: undefined, gte: undefined, lte: undefined });
      return;
    }

    const selectedOption = selectedOptions[0];

    changeTriggerValues(
      input,
      {
        authorType: input.value.authorType,
        eventTypeIn: input.value.eventTypeIn,
        [selectedOption.key]: eventsNumber,
      },
      { overwrite: true }
    );
  };

  return (
    <>
      <Container>
        <Label>of</Label>
        <Picker
          type="multi"
          options={eventOptions}
          selected={selectedEventOptions}
          onSelect={handleEventSelect}
          searchDisabled
          placeholder="Select event type…"
        />
      </Container>
      <Container>
        <Label>by</Label>
        <Picker
          options={authorOptions}
          selected={selectedAuthorOptions}
          onSelect={handleAuthorSelect}
          hideClearButton
          searchDisabled
          placeholder="Select author…"
          css={shortPickerStyles}
        />
      </Container>
      <Container>
        <Label>is</Label>
        <Picker
          options={equalityOptions}
          selected={selectedEqualityOption}
          onSelect={handleEqualityOptionSelect}
          hideClearButton
          searchDisabled
          placeholder="Select equality option…"
          css={shortPickerStyles}
        />
        <NumberInput
          min={0}
          step={1}
          value={eventsNumber.toString()}
          // TODO: remove this once we have a proper type for the onChange handler
          // @ts-expect-error TS(2339)
          onChange={handleEventsNumberChange}
        />
      </Container>
    </>
  );
};

const createAuthorOption = (authorType: string): IPickerListItem => {
  switch (authorType) {
    case Author.Agent:
      return { key: Author.Agent, name: "agent" };
    case Author.Customer:
      return { key: Author.Customer, name: "requester" };
    case Author.System:
      return { key: Author.System, name: "system" };
    case Author.Rule:
      return { key: Author.Rule, name: "rule" };
    default:
      return { key: ANYONE_KEY, name: "anyone" };
  }
};

const createEqualityOption = (operator: string): IPickerListItem => {
  switch (operator) {
    case "gt":
      return { key: operator, name: EqualityLabel[operator] };
    case "gte":
      return { key: operator, name: EqualityLabel[operator] };
    case "lt":
      return { key: operator, name: EqualityLabel[operator] };
    case "lte":
      return { key: operator, name: EqualityLabel[operator] };
    case "eq":
    default:
      return { key: operator, name: EqualityLabel[operator] ?? operator };
  }
};

const getSelectedEqualityOption = (value: IRuleTriggerEventCount): string => {
  if (value.eq !== undefined) return "eq";
  if (value.gt !== undefined) return "gt";
  if (value.lt !== undefined) return "lt";
  if (value.gte !== undefined) return "gte";
  if (value.lte !== undefined) return "lte";
  return "eq";
};

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.div`
  min-width: 40px;
`;

const NumberInput = styled(NumericInput)`
  margin-left: var(${SpacingToken.Spacing2});
`;
